/*
  Supplement test (sections/movitamins-quiz.liquid).
  - Questions are fieldsets; a question shows or hides depending on tags from earlier answers.
  - Every answer adds tags with its question's weight. Products score the weights of the tags they match and are
    picked one by one; tags a picked product already covers count for half after that, so the advice covers
    more than one goal.
  - Notices show for certain tags; a stopping notice replaces the products.
  Answers stay in this browser tab (sessionStorage), so the result is still there after visiting a product.
*/
if (!customElements.get('movitamins-quiz')) {
  const splitTags = (value) =>
    (value || '')
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);

  const storage = {
    read(key) {
      try {
        return JSON.parse(sessionStorage.getItem(key) || 'null');
      } catch (error) {
        return null;
      }
    },
    write(key, value) {
      try {
        sessionStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        // Private mode or blocked storage: the test still works, it just isn't remembered.
      }
    },
    clear(key) {
      try {
        sessionStorage.removeItem(key);
      } catch (error) {
        // Nothing to clear.
      }
    },
  };

  const publish = (name, data) => {
    try {
      window.Shopify?.analytics?.publish?.(name, data);
    } catch (error) {
      // Analytics is optional.
    }
  };

  customElements.define(
    'movitamins-quiz',
    class MovitaminsQuiz extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;

        this.screens = {
          intro: this.querySelector('[data-quiz-screen="intro"]'),
          questions: this.querySelector('[data-quiz-screen="questions"]'),
          result: this.querySelector('[data-quiz-screen="result"]'),
        };
        this.form = this.querySelector('[data-quiz-form]');
        this.steps = [...this.querySelectorAll('[data-quiz-step]')];
        this.products = [...this.querySelectorAll('[data-quiz-product]')];
        this.notices = [...this.querySelectorAll('[data-quiz-notice]')];
        this.hint = this.querySelector('[data-quiz-hint]');
        this.nextButton = this.querySelector('[data-quiz-next]');
        this.nextLabel = this.querySelector('[data-quiz-next-label]');
        this.storageKey = `movitamins-quiz:${window.location.pathname}`;
        this.current = null;

        this.querySelector('[data-quiz-start]')?.addEventListener('click', () => this.start());
        this.querySelector('[data-quiz-back]')?.addEventListener('click', () => this.back());
        this.querySelector('[data-quiz-restart]')?.addEventListener('click', () => this.restart());
        this.form?.addEventListener('submit', (event) => {
          event.preventDefault();
          this.next();
        });
        this.form?.addEventListener('change', (event) => this.onChange(event));
        this.form?.addEventListener('click', (event) => this.onAnswerClick(event));

        this.restore();

        if (window.Shopify?.designMode) {
          document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event));
        }
      }

      /* Steps */

      // Tags from the answered questions before `limit`, as tag -> highest weight.
      tags(limit = this.steps.length) {
        const tags = new Map();
        this.steps.slice(0, limit).forEach((step) => {
          if (!this.isVisible(step, tags)) return;
          const weight = Number(step.dataset.weight) || 0;
          step.querySelectorAll('input:checked').forEach((input) => {
            splitTags(input.dataset.tags).forEach((tag) => {
              tags.set(tag, Math.max(tags.get(tag) ?? 0, weight));
            });
          });
        });
        return tags;
      }

      isVisible(step, tags) {
        const showIf = splitTags(step.dataset.showIf);
        const hideIf = splitTags(step.dataset.hideIf);
        if (showIf.length && !showIf.some((tag) => tags.has(tag))) return false;
        if (hideIf.some((tag) => tags.has(tag))) return false;
        return true;
      }

      visibleSteps() {
        return this.steps.filter((step, index) => this.isVisible(step, this.tags(index)));
      }

      answered(step) {
        return Boolean(step?.querySelector('input:checked'));
      }

      show(name) {
        Object.entries(this.screens).forEach(([key, screen]) => {
          if (screen) screen.hidden = key !== name;
        });
        this.classList.toggle('is-result', name === 'result');
      }

      start() {
        publish('movitamins_quiz_started', {});
        this.goTo(this.visibleSteps()[0]);
      }

      goTo(step, { focus = true } = {}) {
        if (!step) return;
        this.show('questions');
        this.current = step;
        this.steps.forEach((item) => {
          item.hidden = item !== step;
        });

        const visible = this.visibleSteps();
        const index = visible.indexOf(step);
        const label = this.querySelector('[data-quiz-progress-label]');
        if (label) {
          label.textContent = (this.dataset.progress || '')
            .replace('[current]', index + 1)
            .replace('[total]', visible.length);
        }
        const bar = this.querySelector('[data-quiz-progress-bar]');
        if (bar) bar.style.width = `${((index + 1) / visible.length) * 100}%`;

        const last = index === visible.length - 1;
        if (this.nextLabel) {
          this.nextLabel.textContent = last ? this.nextLabel.dataset.finish : this.nextLabel.dataset.next;
        }
        this.setHint('');
        this.updateLimits(step);
        this.updateNext();
        this.save();

        if (focus) {
          this.scrollToTop();
          step.querySelector('[data-quiz-title]')?.focus({ preventScroll: true });
        }
      }

      next() {
        const step = this.current;
        if (!step) return;
        if (!this.answered(step)) {
          this.setHint(this.dataset.chooseHint || '');
          return;
        }
        const visible = this.visibleSteps();
        const index = visible.indexOf(step);
        if (index < visible.length - 1) {
          this.goTo(visible[index + 1]);
        } else {
          this.finish();
        }
      }

      back() {
        const visible = this.visibleSteps();
        const index = visible.indexOf(this.current);
        if (index > 0) {
          this.goTo(visible[index - 1]);
        } else {
          this.current = null;
          this.show('intro');
          this.save();
          this.scrollToTop();
          this.querySelector('[data-quiz-start]')?.focus({ preventScroll: true });
        }
      }

      restart() {
        this.form?.reset();
        this.steps.forEach((step) => this.updateLimits(step));
        storage.clear(this.storageKey);
        this.goTo(this.visibleSteps()[0]);
      }

      onChange(event) {
        const step = event.target.closest('[data-quiz-step]');
        if (!step) return;
        this.updateLimits(step);
        this.updateNext();
        this.setHint('');
        this.save();
      }

      // A mouse or touch choice on a single-answer question moves on by itself, except on the last question.
      // Keyboard users (arrow keys also fire click, with detail 0) move on with the button.
      onAnswerClick(event) {
        const input = event.target.closest('input[type="radio"]');
        if (!input || event.detail === 0) return;
        const step = input.closest('[data-quiz-step]');
        if (step !== this.current) return;
        const visible = this.visibleSteps();
        if (visible.indexOf(step) === visible.length - 1) return;
        clearTimeout(this.autoNext);
        this.autoNext = setTimeout(() => {
          if (this.current === step && input.checked) this.next();
        }, 280);
      }

      updateLimits(step) {
        if (step.dataset.multiple !== 'true') return;
        const max = Number(step.dataset.max) || 0;
        const boxes = [...step.querySelectorAll('input[type="checkbox"]')];
        const checked = boxes.filter((box) => box.checked).length;
        boxes.forEach((box) => {
          const full = max > 0 && checked >= max && !box.checked;
          box.disabled = full;
          box.closest('.mvq-answer')?.classList.toggle('is-disabled', full);
        });
      }

      updateNext() {
        if (this.nextButton) this.nextButton.setAttribute('aria-disabled', String(!this.answered(this.current)));
      }

      setHint(text) {
        if (this.hint) this.hint.textContent = text;
      }

      scrollToTop() {
        const top = this.getBoundingClientRect().top;
        if (top < 0) {
          const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
          window.scrollBy({ top: top - 24, behavior });
        }
      }

      /* Result */

      finish({ focus = true } = {}) {
        const tags = this.tags();
        const has = (tag) => tags.has(tag);

        const notices = this.notices.filter((notice) => {
          const show = splitTags(notice.dataset.tags).some(has);
          const hide = splitTags(notice.dataset.exclude).some(has);
          return show && !hide;
        });
        const stop = notices.some((notice) => notice.dataset.stop === 'true');
        this.notices.forEach((notice) => {
          notice.hidden = !notices.includes(notice);
        });

        const picked = stop ? [] : this.pick(tags);
        this.renderProducts(picked);

        const title = this.querySelector('[data-quiz-result-title]');
        const intro = this.querySelector('[data-quiz-result-intro]');
        const empty = this.querySelector('[data-quiz-empty]');
        const state = stop ? 'stop' : picked.length ? 'default' : 'empty';
        if (title) title.textContent = title.dataset[state] || title.dataset.default;
        if (intro) intro.hidden = state !== 'default';
        if (empty) empty.hidden = state !== 'empty';

        const answers = this.answerLabels();
        const productTitles = picked.map((item) => item.element.dataset.title);
        this.renderSummary(answers);
        this.updateAdviceLinks(answers, productTitles);

        this.current = null;
        this.show('result');
        this.save(true);
        publish('movitamins_quiz_completed', {
          products: picked.map((item) => item.element.dataset.quizProduct),
          stopped: stop,
        });

        if (focus) {
          this.scrollToTop();
          title?.focus({ preventScroll: true });
        }
      }

      // Scores every product and picks them one at a time. Each tag a picked product covers counts for half
      // for the next pick, so a second goal gets its own product.
      pick(tags) {
        const limit = Number(this.dataset.results) || 3;
        const covered = new Map();
        const weightOf = (match) => {
          const parts = match.split('+');
          if (!parts.every((tag) => tags.has(tag))) return null;
          return Math.max(...parts.map((tag) => tags.get(tag)));
        };
        const coverage = (match) => Math.max(...match.split('+').map((tag) => covered.get(tag) || 0));

        const candidates = this.products
          .filter((element) => element.dataset.available === 'true')
          .filter((element) => splitTags(element.dataset.require).every((tag) => tags.has(tag)))
          .filter((element) => !splitTags(element.dataset.exclude).some((tag) => tags.has(tag)))
          .map((element) => ({
            element,
            boost: Number(element.dataset.boost) || 0,
            matches: [...element.querySelectorAll('[data-match]')]
              .map((item) => ({ item, key: item.dataset.match, weight: weightOf(item.dataset.match) }))
              .filter((match) => match.weight !== null),
          }))
          .filter((candidate) => candidate.matches.length);

        const picked = [];
        while (picked.length < limit) {
          let best = null;
          let bestScore = 0;
          candidates.forEach((candidate) => {
            if (picked.includes(candidate)) return;
            const score = candidate.matches.reduce(
              (sum, match) => sum + match.weight * 0.5 ** coverage(match.key),
              0
            );
            if (score <= 0) return;
            const total = score + candidate.boost * 0.5;
            if (total > bestScore) {
              best = candidate;
              bestScore = total;
            }
          });
          if (!best) break;
          picked.push(best);
          best.matches.forEach((match) => {
            if (match.weight <= 0) return;
            match.key.split('+').forEach((tag) => covered.set(tag, (covered.get(tag) || 0) + 1));
          });
        }
        return picked;
      }

      renderProducts(picked) {
        const list = this.querySelector('[data-quiz-products]');
        const reasonsLimit = Number(this.dataset.reasons) || 2;
        this.products.forEach((element) => {
          element.hidden = true;
        });
        picked.forEach((candidate, index) => {
          const { element } = candidate;
          element.hidden = false;
          list?.append(element);
          element.classList.toggle('is-best', index === 0 && picked.length > 1);
          const badge = element.querySelector('[data-quiz-best]');
          if (badge) badge.hidden = !(index === 0 && picked.length > 1);

          // Reasons with text, strongest first, then in the order they were written.
          const reasons = candidate.matches
            .filter((match) => match.item.textContent.trim())
            .sort((a, b) => b.weight - a.weight);
          const shown = new Set(reasons.slice(0, reasonsLimit).map((match) => match.item));
          element.querySelectorAll('[data-match]').forEach((item) => {
            item.hidden = !shown.has(item);
          });
          const fallback = element.querySelector('[data-fallback]');
          if (fallback) fallback.hidden = shown.size > 0;
        });
      }

      // "Vette vis: minder vaak of nooit" reads better than a bare "Minder vaak of nooit".
      answerLabels() {
        return this.visibleSteps().flatMap((step) =>
          [...step.querySelectorAll('input:checked')].map((input) => {
            const label = input.dataset.label;
            const summary = step.dataset.summary;
            return summary ? `${summary}: ${label.charAt(0).toLowerCase()}${label.slice(1)}` : label;
          })
        );
      }

      renderSummary(answers) {
        const list = this.querySelector('[data-quiz-summary]');
        if (!list) return;
        list.replaceChildren(
          ...answers.map((answer) => {
            const item = document.createElement('li');
            item.textContent = answer;
            return item;
          })
        );
      }

      updateAdviceLinks(answers, products) {
        const fill = (template) =>
          (template || '')
            .replace('[antwoorden]', answers.join('; '))
            .replace('[producten]', products.length ? products.join(', ') : '-');

        const whatsapp = this.querySelector('[data-quiz-whatsapp]');
        const base = (this.dataset.whatsapp || '').trim();
        if (whatsapp && base) {
          const message = fill(this.dataset.whatsappMessage);
          whatsapp.href = message ? `${base}?text=${encodeURIComponent(message)}` : base;
        }

        const advice = this.querySelector('[data-quiz-advice-link]');
        const adviceUrl = (this.dataset.adviceUrl || '').trim();
        if (advice && adviceUrl) {
          const message = fill(this.dataset.adviceMessage);
          if (message) {
            const url = new URL(adviceUrl, window.location.origin);
            url.searchParams.set('bericht', message);
            advice.href = `${url.pathname}${url.search}`;
          }
        }
      }

      /* State */

      save(done = false) {
        const answers = {};
        this.steps.forEach((step) => {
          const values = [...step.querySelectorAll('input:checked')].map((input) => input.value);
          if (values.length) answers[step.dataset.quizStep] = values;
        });
        storage.write(this.storageKey, {
          answers,
          step: this.current?.dataset.quizStep || null,
          done: done || (this.screens.result && !this.screens.result.hidden),
        });
      }

      restore() {
        const saved = storage.read(this.storageKey);
        if (!saved || !saved.answers) return;
        this.steps.forEach((step) => {
          const values = saved.answers[step.dataset.quizStep] || [];
          step.querySelectorAll('input').forEach((input) => {
            input.checked = values.includes(input.value);
          });
          this.updateLimits(step);
        });
        if (saved.done) {
          this.finish({ focus: false });
          return;
        }
        const step = this.steps.find((item) => item.dataset.quizStep === saved.step);
        if (step && this.visibleSteps().includes(step)) this.goTo(step, { focus: false });
      }

      /* Theme editor: show the selected question, or all products with their reasons. */
      onBlockSelect(event) {
        if (!this.contains(event.target)) return;
        const step = event.target.closest('[data-quiz-step]');
        if (step) {
          this.goTo(step, { focus: false });
          step.hidden = false;
          return;
        }
        this.show('result');
        this.products.forEach((element) => {
          element.hidden = false;
          element.querySelectorAll('[data-match]').forEach((item) => {
            item.hidden = false;
          });
        });
        this.notices.forEach((notice) => {
          notice.hidden = false;
        });
        this.querySelectorAll('[data-quiz-editor-note]').forEach((note) => {
          note.hidden = false;
        });
      }
    }
  );
}
